import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { z } from 'zod'

const submitSchema = z.object({
  url: z.string().url('Enter a valid URL'),
  org_id: z.string().min(1, 'Select an organisation'),
  content: z.string().min(200, 'Content must be at least 200 characters'),
})

type SubmitState = 'idle' | 'checking' | 'blocked' | 'success'

export function PortalSubmitView() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [form, setForm] = useState({ url: '', org_id: '', content: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [state, setState] = useState<SubmitState>('idle')
  const [blockInfo, setBlockInfo] = useState<{ score: number; date: string } | null>(null)

  const { data: accreditations } = useQuery({
    queryKey: ['my-active-accreditations'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select('org_id, organizations(name)')
        .eq('journalist_id', user.id)
        .eq('status', 'active') as any)
      return (data ?? []) as any[]
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const result = submitSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message
      })
      setErrors(fieldErrors)
      return
    }

    setState('checking')
    try {
      const { data, error } = await supabase.functions.invoke('submit-journalist-content', {
        body: {
          url: form.url,
          org_id: form.org_id,
          content: form.content,
        }
      })
      if (error) throw error

      if (data?.blocked) {
        setBlockInfo({
          score: Math.round((data.similarity ?? 0) * 100),
          date: data.conflict_date ?? '',
        })
        setState('blocked')
      } else {
        setState('success')
        toast({ title: 'Content submitted successfully.' })
        setTimeout(() => navigate('/portal'), 1500)
      }
    } catch (err: any) {
      toast({ title: err.message || 'Submission failed.', variant: 'destructive' })
      setState('idle')
    }
  }

  if (state === 'success') {
    return (
      <div className="max-w-xl mx-auto text-center py-20">
        <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-foreground">Submission accepted</h2>
        <p className="text-justify text-muted-foreground text-sm mt-2">Redirecting to your dashboard…</p>
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Submit Content</h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">
          Submit published content to an accredited organisation for coverage tracking.
        </p>
      </div>

      {state === 'blocked' && blockInfo && (
        <div className="bg-[hsl(var(--destructive)/0.08)] border border-[hsl(var(--destructive)/0.3)] rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
          <div>
            <p className="text-justify text-destructive font-medium text-sm">Submission could not be accepted</p>
            <p className="text-destructive text-sm mt-1 text-justify">
              Your submission could not be accepted because it closely matches existing content
              on the platform. Similarity: {blockInfo.score}%. Please ensure all submitted
              content is original.
            </p>
            <p className="text-justify text-destructive text-xs mt-2">
              A submission from another journalist on {blockInfo.date
                ? new Date(blockInfo.date).toLocaleDateString()
                : 'a previous date'} was found to be similar.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 text-xs"
              onClick={() => setState('idle')}
            >
              Edit and resubmit
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 bg-background rounded-xl border p-6">
        <div className="space-y-1.5">
          <Label>Published URL</Label>
          <Input
            type="url"
            value={form.url}
            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            placeholder="https://example.com/your-article"
          />
          {errors.url && <p className="text-justify text-destructive text-xs">{errors.url}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Organisation</Label>
          <Select value={form.org_id} onValueChange={v => setForm(f => ({ ...f, org_id: v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select an accredited organisation" />
            </SelectTrigger>
            <SelectContent>
              {(accreditations ?? []).map((acc: any) => (
                <SelectItem key={acc.org_id} value={acc.org_id}>
                  {acc.organizations?.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.org_id && <p className="text-justify text-destructive text-xs">{errors.org_id}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Content Body</Label>
          <Textarea
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Paste the full text of your article or content here (minimum 200 characters)…"
            rows={8}
            className="resize-y"
          />
          <div className="flex items-center justify-between">
            {errors.content && <p className="text-justify text-destructive text-xs">{errors.content}</p>}
            <p className={`text-xs ml-auto ${form.content.length >= 200 ? 'text-success' : 'text-muted-foreground'}`}>
              {form.content.length} / 200 min
            </p>
          </div>
        </div>

        <Button
          type="submit"
          disabled={state === 'checking'}
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {state === 'checking' ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" />Checking for duplicate content…</>
          ) : 'Submit Content'}
        </Button>
      </form>
    </div>
  )
}
